import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LuArrowLeft, LuLoader2 } from "react-icons/lu";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import Avatar from "../../app-components/ui/Avatar";
import PageHeader from "../../app-components/ui/PageHeader";
import { btnPrimary, btnSecondary, card, inputBase } from "../../app-components/ui/styles";
import { SITELINKS } from "../../app-routes/links";
import { getErrorMessage } from "../../utils";
import notify from "../../utils/notify";

/** Editable fields (matches PATCH /v1/admin/users/{id}; every field is optional) */
type Form = {
  full_name: string; username: string; email: string; phone_number: string;
  DOB: string; gender: string; country_id: string; state_id: string; city_id: string; area: string;
  bio: string; interest: string; instagram_id: string; snapchat_id: string; twitter_x_id: string;
  is_private: boolean; is_email_notification: boolean;
};
type Option = { id: number; name: string };

const EMPTY: Form = {
  full_name: "", username: "", email: "", phone_number: "", DOB: "", gender: "", country_id: "", state_id: "", city_id: "",
  area: "", bio: "", interest: "", instagram_id: "", snapchat_id: "", twitter_x_id: "", is_private: false, is_email_notification: true,
};

const yes = (v: unknown) => v !== null && v !== undefined && !["", "0", "false", "no"].includes(String(v).toLowerCase());

/** API record -> form values (ids as strings for the selects, dates as YYYY-MM-DD) */
const toForm = (u: any): Form => ({
  full_name: u.full_name ?? "", username: u.username ?? "", email: u.email ?? "", phone_number: u.phone_number ?? "",
  DOB: u.DOB ? String(u.DOB).slice(0, 10) : "", gender: u.gender ?? "",
  country_id: u.country_id ? String(u.country_id) : "", state_id: u.state_id ? String(u.state_id) : "", city_id: u.city_id ? String(u.city_id) : "",
  area: u.area ?? "", bio: u.bio ?? "", interest: u.interest ?? "",
  instagram_id: u.instagram_id ?? "", snapchat_id: u.snapchat_id ?? "", twitter_x_id: u.twitter_x_id ?? "",
  is_private: yes(u.is_private), is_email_notification: u.is_email_notification === undefined ? true : yes(u.is_email_notification),
});

/** Only what changed, in the types the API expects (numbers for ids, null for cleared) */
const changes = (from: Form, to: Form) => {
  const out: Record<string, unknown> = {};
  (Object.keys(to) as (keyof Form)[]).forEach((k) => {
    if (from[k] === to[k]) return;
    const v = to[k];
    if (typeof v === "boolean") out[k] = v;
    else if (k.endsWith("_id") && ["country_id", "state_id", "city_id"].includes(k)) out[k] = v ? Number(v) : null;
    else out[k] = typeof v === "string" && v.trim() === "" ? null : typeof v === "string" ? v.trim() : v;
  });
  return out;
};

const FIELD_LABELS: Partial<Record<keyof Form, string>> = {
  full_name: "Full name", username: "Username", email: "Email", phone_number: "Phone", DOB: "Date of birth", gender: "Gender",
  country_id: "Country", state_id: "State", city_id: "City", area: "Area", bio: "Bio", interest: "Interest",
  instagram_id: "Instagram", snapchat_id: "Snapchat", twitter_x_id: "X (Twitter)",
};

const Section = ({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) => (
  <section className={`${card} p-5`}>
    <h2 className="text-[15px] font-semibold text-honeConnectBlack">{title}</h2>
    {hint && <p className="mt-0.5 text-xs text-hcMuted">{hint}</p>}
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
  </section>
);

const UpdateUser = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [original, setOriginal] = useState<Form>(EMPTY);
  const [form, setForm] = useState<Form>(EMPTY);
  const [display, setDisplay] = useState<{ name: string; photo: string | null }>({ name: "", photo: null });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [countries, setCountries] = useState<Option[]>([]);
  const [states, setStates] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiClient.get(`users/${id}`);
        const f = toForm(res.data);
        setOriginal(f);
        setForm(f);
        setDisplay({ name: res.data.linkedin_full_name || res.data.full_name || res.data.username || "Member", photo: res.data.linkedin_photo || res.data.profile_pix_link });
      } catch (err) {
        setLoadError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
    apiClient.get("countries").then((r: any) => setCountries(r?.data?.data ?? r?.data ?? [])).catch(() => undefined);
  }, [id]);

  useEffect(() => {
    setStates([]);
    if (form.country_id) apiClient.get(`countries/${form.country_id}/states`).then((r: any) => setStates(r?.data?.data ?? r?.data ?? [])).catch(() => undefined);
  }, [form.country_id]);
  useEffect(() => {
    setCities([]);
    if (form.state_id) apiClient.get(`states/${form.state_id}/cities`).then((r: any) => setCities(r?.data?.data ?? r?.data ?? [])).catch(() => undefined);
  }, [form.state_id]);

  const diff = useMemo(() => changes(original, form), [original, form]);
  const dirty = Object.keys(diff).length > 0;

  // warn before leaving with unsaved changes (closing the tab / reloading)
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "country_id") { next.state_id = ""; next.city_id = ""; } // location cascades
      if (key === "state_id") next.city_id = "";
      return next;
    });
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof Form, string>> = {};
    if ("full_name" in diff && !form.full_name.trim()) e.full_name = "Full name can't be empty.";
    if ("username" in diff && !form.username.trim()) e.username = "Username can't be empty.";
    if ("email" in diff && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email address.";
    if ("DOB" in diff && form.DOB && new Date(form.DOB) >= new Date()) e.DOB = "Date of birth must be in the past.";
    if (form.bio.length > 500) e.bio = "Bio can be at most 500 characters.";
    if (form.phone_number.length > 20) e.phone_number = "Phone number is too long.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!dirty || !validate()) return;
    setSaving(true);
    try {
      // 422 resolves here (instead of throwing) so each error can be shown beside its field
      const res: any = await apiClient.patch(`users/${id}`, diff, { validateStatus: (s: number) => s < 500 && s !== 401 && s !== 403 });
      if (res.status === 422 && res.data?.errors) {
        const fieldErrors: Partial<Record<keyof Form, string>> = {};
        Object.entries(res.data.errors as Record<string, string[]>).forEach(([k, v]) => { fieldErrors[k as keyof Form] = v[0]; });
        setErrors(fieldErrors);
        notify.error("Some fields need attention.");
        return;
      }
      if (res.status >= 400) {
        notify.error(res.data?.message ?? "Couldn't save the changes.");
        return;
      }
      const saved = Object.keys(diff).map((k) => FIELD_LABELS[k as keyof Form] ?? (k === "is_private" ? "Visibility" : "Email notifications"));
      notify.success(`Saved: ${saved.join(", ")}.`);
      setOriginal(form);
      navigate(`${SITELINKS.USER_PROFILE}${id}`);
    } catch (err) {
      notify.error(`Couldn't save the changes: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const input = (key: keyof Form, label: string, props: Record<string, unknown> = {}) => (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-hcMuted">{label}</span>
      <input
        value={String(form[key] ?? "")}
        onChange={(e) => set(key, e.target.value as never)}
        aria-invalid={!!errors[key]}
        className={`${inputBase} w-full ${errors[key] ? "border-red-400" : ""} ${key in diff ? "border-honeConnectPurple/60" : ""}`}
        {...props}
      />
      {errors[key] && <span className="mt-1 block text-xs text-red-600">{errors[key]}</span>}
    </label>
  );

  const select = (key: keyof Form, label: string, options: { value: string; label: string }[], disabled = false, placeholder = "Not set") => (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-hcMuted">{label}</span>
      <select value={String(form[key] ?? "")} onChange={(e) => set(key, e.target.value as never)} disabled={disabled}
        className={`${inputBase} w-full ${errors[key] ? "border-red-400" : ""} ${key in diff ? "border-honeConnectPurple/60" : ""}`}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {errors[key] && <span className="mt-1 block text-xs text-red-600">{errors[key]}</span>}
    </label>
  );

  const toggle = (key: "is_private" | "is_email_notification", label: string, hint: string) => (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-hcBorder p-3.5 sm:col-span-1">
      <span><span className="block text-sm font-medium text-honeConnectBlack">{label}</span><span className="text-xs text-hcMuted">{hint}</span></span>
      <span className="relative mt-0.5 inline-flex shrink-0">
        <input type="checkbox" role="switch" checked={form[key]} onChange={(e) => set(key, e.target.checked)} className="peer sr-only" />
        <span className="h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-honeConnectPurple peer-focus-visible:ring-2 peer-focus-visible:ring-honeConnectPurple/40" />
        <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
      </span>
    </label>
  );

  return (
    <AdminLayout title={display.name ? `Admin | Edit ${display.name}` : "Admin | Edit member"}>
      <Link to={`${SITELINKS.USER_PROFILE}${id}`} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-honeConnectGray hover:text-honeConnectPurple">
        <LuArrowLeft className="h-4 w-4" aria-hidden /> Back to profile
      </Link>

      {loading ? (
        <div className={`${card} flex justify-center py-16`}><LuLoader2 className="h-6 w-6 animate-spin text-hcMuted" aria-hidden /></div>
      ) : loadError ? (
        <div className={`${card} px-6 py-14 text-center`}>
          <p className="mb-1 font-semibold text-honeConnectBlack">Couldn't load this member</p>
          <p className="text-sm text-hcMuted">{loadError}</p>
        </div>
      ) : (
        <form onSubmit={save} noValidate>
          <PageHeader
            title="Edit profile"
            subtitle="Only the fields you change are saved. Fields left empty stay empty."
          />
          <div className={`${card} mb-5 flex items-center gap-3 p-4`}>
            <Avatar name={display.name} src={display.photo} size={44} />
            <div>
              <p className="font-semibold text-honeConnectBlack">{display.name}</p>
              <p className="text-xs text-hcMuted">Photos come from the app and LinkedIn, so they can't be changed here.</p>
            </div>
          </div>

          <div className="space-y-5">
            <Section title="Identity">
              {input("full_name", "Full name", { autoComplete: "off" })}
              {input("username", "Username", { autoComplete: "off" })}
              {input("email", "Email", { type: "email", autoComplete: "off" })}
              {input("phone_number", "Phone", { inputMode: "tel", autoComplete: "off" })}
            </Section>

            <Section title="Personal">
              {input("DOB", "Date of birth", { type: "date", max: new Date().toISOString().slice(0, 10) })}
              {select("gender", "Gender", [{ value: "MALE", label: "Male" }, { value: "FEMALE", label: "Female" }, { value: "OTHERS", label: "Other" }])}
            </Section>

            <Section title="Location" hint="Choose the country first, then the state and city.">
              {select("country_id", "Country", countries.map((c) => ({ value: String(c.id), label: c.name })))}
              {select("state_id", "State", states.map((s) => ({ value: String(s.id), label: s.name })), !form.country_id, form.country_id ? "Not set" : "Choose a country first")}
              {select("city_id", "City", cities.map((c) => ({ value: String(c.id), label: c.name })), !form.state_id, form.state_id ? "Not set" : "Choose a state first")}
              {input("area", "Area / neighbourhood")}
            </Section>

            <Section title="Profile">
              <label className="block text-sm sm:col-span-2">
                <span className="mb-1 flex justify-between text-xs font-medium text-hcMuted"><span>Bio</span><span className={form.bio.length > 500 ? "text-red-600" : ""}>{form.bio.length}/500</span></span>
                <textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={4}
                  className={`${inputBase} w-full resize-y ${errors.bio ? "border-red-400" : ""}`} />
                {errors.bio && <span className="mt-1 block text-xs text-red-600">{errors.bio}</span>}
              </label>
              {input("interest", "Main interest")}
            </Section>

            <Section title="Social">
              {input("instagram_id", "Instagram")}
              {input("snapchat_id", "Snapchat")}
              {input("twitter_x_id", "X (Twitter)")}
            </Section>

            <Section title="Preferences">
              {toggle("is_private", "Private profile", "Hide this member from public discovery in the app.")}
              {toggle("is_email_notification", "Email notifications", "Send this member email updates.")}
            </Section>
          </div>

          {/* sticky save bar */}
          <div className="sticky bottom-0 z-10 -mx-4 mt-5 border-t border-hcBorder bg-white/95 px-4 py-3 backdrop-blur sm:-mx-7 sm:px-7">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-hcMuted">
                {dirty ? `${Object.keys(diff).length} unsaved change${Object.keys(diff).length === 1 ? "" : "s"}` : "No changes yet"}
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => { setForm(original); setErrors({}); }} disabled={!dirty || saving} className={btnSecondary}>Discard</button>
                <button type="submit" disabled={!dirty || saving} className={`${btnPrimary} inline-flex items-center gap-2`}>
                  {saving && <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden />}Save changes
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </AdminLayout>
  );
};

export default UpdateUser;
